import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { ArrowLeft, Pencil, Trash2, X, UserMinus, UserPlus } from "lucide-react";
import ClientAutocomplete from "@/components/ClientAutocomplete";

export default function CollaboratorDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [c, setC] = useState(null);
    const [clients, setClients] = useState([]);
    const [allClients, setAllClients] = useState([]);
    const [showEdit, setShowEdit] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showAdd, setShowAdd] = useState(false);

    const load = async () => {
        const [a, b, all] = await Promise.all([
            api.get(`/collaborators/${id}`),
            api.get(`/collaborators/${id}/clients`),
            api.get(`/clients`),
        ]);
        setC(a.data); setClients(b.data); setAllClients(all.data);
    };
    useEffect(() => { load(); }, [id]);  // eslint-disable-line

    const removeFromFolder = async (cl) => {
        if (!window.confirm(`Rimuovere ${cl.first_name} ${cl.last_name} dalla cartella? Rimarrà come cliente normale.`)) return;
        await api.patch(`/clients/${cl.id}`, { ...cl, collaborator_id: null });
        load();
    };

    const addToFolder = async (cl) => {
        await api.patch(`/clients/${cl.id}`, { ...cl, collaborator_id: id });
        setShowAdd(false);
        load();
    };

    const doDelete = async (mode) => {
        await api.delete(`/collaborators/${id}?client_action=${mode}`);
        nav("/collaboratori");
    };

    if (!c) return <div className="p-6 text-sm text-neutral-500">Caricamento…</div>;
    const outsideClients = allClients.filter((x) => x.collaborator_id !== id);

    return (
        <div className="p-6" data-testid="collab-detail">
            <div className="flex items-center gap-3 mb-4">
                <Link to="/collaboratori" className="text-neutral-500 hover:text-neutral-800"><ArrowLeft size={16} /></Link>
                <div>
                    <div className="text-xs text-neutral-500">Collaboratore</div>
                    <h1 className="font-display text-2xl font-semibold tracking-tight text-neutral-900">{c.name}</h1>
                </div>
                <div className="ml-auto flex items-center gap-2">
                    <button onClick={() => setShowEdit(true)} data-testid="edit-collab-btn" className="text-sm border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 rounded px-2.5 py-1 inline-flex items-center gap-1">
                        <Pencil size={13} /> Modifica
                    </button>
                    <button onClick={() => setShowDelete(true)} data-testid="delete-collab-btn" className="text-sm border border-red-200 bg-white text-red-600 hover:bg-red-50 rounded px-2.5 py-1 inline-flex items-center gap-1">
                        <Trash2 size={13} /> Elimina
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border border-neutral-200 rounded-md p-4 md:col-span-1 space-y-2">
                    <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">Contatti</div>
                    <Row label="Email">{c.email || "—"}</Row>
                    <Row label="Telefono">{c.phone || "—"}</Row>
                    <Row label="Accordo">{c.commission || "—"}</Row>
                    {c.notes && <div className="text-sm text-neutral-700 mt-2 whitespace-pre-line">{c.notes}</div>}
                </div>
                <div className="bg-white border border-neutral-200 rounded-md md:col-span-2 overflow-hidden">
                    <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between">
                        <div className="font-display text-sm font-semibold">Clienti · {clients.length}</div>
                        <button onClick={() => setShowAdd(true)} data-testid="add-client-to-folder-btn" className="text-xs bg-neutral-900 text-white px-2 py-1 rounded inline-flex items-center gap-1 hover:bg-neutral-800">
                            <UserPlus size={12} /> Aggiungi cliente
                        </button>
                    </div>
                    {clients.length === 0 ? (
                        <div className="p-6 text-sm text-neutral-400">Nessun cliente nella cartella.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <tbody>
                                {clients.map((cl) => (
                                    <tr key={cl.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`folder-client-${cl.id}`}>
                                        <td className="px-4 py-2">
                                            <Link className="font-medium text-neutral-900 hover:underline" to={`/clienti/${cl.id}`}>{cl.first_name} {cl.last_name}</Link>
                                            <div className="text-xs text-neutral-500">{cl.company || "—"}</div>
                                        </td>
                                        <td className="px-4 py-2 text-xs text-neutral-500 text-right">{cl.email || ""}</td>
                                        <td className="px-4 py-2 text-right">
                                            <button onClick={() => removeFromFolder(cl)} data-testid={`remove-from-folder-${cl.id}`} className="text-xs text-neutral-400 hover:text-red-600 inline-flex items-center gap-1" title="Rimuovi dalla cartella">
                                                <UserMinus size={13} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showEdit && (
                <EditCollabModal collab={c} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); }} />
            )}
            {showDelete && (
                <DeleteCollabModal count={clients.length} name={c.name}
                    onClose={() => setShowDelete(false)}
                    onConfirm={(mode) => doDelete(mode)} />
            )}
            {showAdd && (
                <div className="fixed inset-0 bg-neutral-900/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md shadow-lg w-full max-w-md" data-testid="add-to-folder-modal">
                        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
                            <div className="font-display font-semibold">Aggiungi cliente alla cartella</div>
                            <button onClick={() => setShowAdd(false)}><X size={16} /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <div className="text-xs text-neutral-500">
                                Cerca un cliente esistente per aggiungerlo alla cartella, oppure digita un nome nuovo per crearlo direttamente qui.
                            </div>
                            <ClientAutocomplete
                                clients={allClients.filter((x) => x.collaborator_id !== id)}
                                value=""
                                onChange={(cid) => {
                                    if (!cid) return;
                                    const cl = allClients.find((x) => x.id === cid);
                                    if (cl) addToFolder(cl);
                                }}
                                onClientCreated={(newCl) => {
                                    // newly quick-created client already has collaborator_id set via `collaboratorId` prop
                                    setShowAdd(false); load();
                                }}
                                collaboratorId={id}
                                placeholder="Cerca cliente esistente o scrivi un nuovo nome…"
                                testId="folder-client-autocomplete"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function EditCollabModal({ collab, onClose, onSaved }) {
    const [form, setForm] = useState({ ...collab });
    const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
    const submit = async (e) => {
        e.preventDefault(); setBusy(true); setErr("");
        try { await api.patch(`/collaborators/${collab.id}`, form); onSaved(); }
        catch (e) { setErr(extractError(e)); } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-neutral-900/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="edit-collab-modal" className="bg-white rounded-md shadow-lg w-full max-w-md">
                <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
                    <div className="font-display font-semibold">Modifica collaboratore</div>
                    <button type="button" onClick={onClose}><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3">
                    <L label="Nome*"><input required data-testid="edit-collab-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm" /></L>
                    <L label="Email"><input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm" /></L>
                    <L label="Telefono"><input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm" /></L>
                    <L label="Accordo / provvigione"><input value={form.commission || ""} onChange={(e) => setForm({ ...form, commission: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm" /></L>
                    <L label="Note"><textarea rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm" /></L>
                </div>
                {err && <div className="mx-5 mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
                <div className="px-5 py-3 border-t border-neutral-100 flex justify-end gap-2 bg-neutral-50">
                    <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-neutral-300 bg-white">Annulla</button>
                    <button type="submit" disabled={busy} data-testid="save-collab-btn" className="px-3 py-1.5 text-sm rounded bg-neutral-900 text-white hover:bg-neutral-800">Salva</button>
                </div>
            </form>
        </div>
    );
}

function DeleteCollabModal({ name, count, onClose, onConfirm }) {
    const [mode, setMode] = useState("detach");
    return (
        <div className="fixed inset-0 bg-neutral-900/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md shadow-lg w-full max-w-md" data-testid="delete-collab-modal">
                <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
                    <div className="font-display font-semibold">Eliminare il collaboratore?</div>
                    <button onClick={onClose}><X size={16} /></button>
                </div>
                <div className="p-5 text-sm text-neutral-700 space-y-3">
                    <p>Stai per eliminare <b>{name}</b>.</p>
                    {count > 0 ? (
                        <>
                            <p>Questa cartella contiene <b>{count}</b> client{count === 1 ? "e" : "i"}. Cosa vuoi fare?</p>
                            <label className="flex items-start gap-2 p-3 border border-neutral-200 rounded cursor-pointer hover:bg-neutral-50">
                                <input type="radio" name="collab-mode" value="detach" checked={mode === "detach"} onChange={() => setMode("detach")} data-testid="delete-collab-mode-detach" />
                                <div>
                                    <div className="font-medium">Conserva i clienti</div>
                                    <div className="text-xs text-neutral-500">I clienti restano nell'archivio come clienti normali, senza collaboratore.</div>
                                </div>
                            </label>
                            <label className="flex items-start gap-2 p-3 border border-neutral-200 rounded cursor-pointer hover:bg-neutral-50">
                                <input type="radio" name="collab-mode" value="delete" checked={mode === "delete"} onChange={() => setMode("delete")} data-testid="delete-collab-mode-delete" />
                                <div>
                                    <div className="font-medium text-red-700">Elimina anche i clienti</div>
                                    <div className="text-xs text-neutral-500">Tutti i clienti nella cartella verranno eliminati.</div>
                                </div>
                            </label>
                        </>
                    ) : (
                        <p className="text-neutral-500">La cartella è vuota.</p>
                    )}
                </div>
                <div className="px-5 py-3 border-t border-neutral-100 flex justify-end gap-2 bg-neutral-50">
                    <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-neutral-300 bg-white">Annulla</button>
                    <button onClick={() => onConfirm(mode)} data-testid="confirm-delete-collab" className="px-3 py-1.5 text-sm rounded bg-red-600 text-white hover:bg-red-700">Elimina definitivamente</button>
                </div>
            </div>
        </div>
    );
}

function Row({ label, children }) { return <div className="flex items-baseline text-sm"><div className="w-20 text-xs text-neutral-500">{label}</div><div className="text-neutral-800">{children}</div></div>; }
function L({ label, children }) { return <label className="block"><span className="text-xs font-medium text-neutral-600">{label}</span><div className="mt-1">{children}</div></label>; }
